import { useState } from 'react';
import { z } from 'zod';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { toast } from '@/design-system/primitives/Toast/Toast';
import { useFileUpload } from '@/lib/useFileUpload';

/**
 * Internal showcase route for the F0 primitives. Lives at `/dev/primitives`
 * (development-only, no production link). Each section is an isolated demo
 * of one primitive so they can be eyeballed without booking a real flow.
 */
export function DevPrimitives() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const upload = useFileUpload({ accept: 'image/*', multiple: true, maxBytes: 5 * 1024 * 1024 });

  return (
    <AppFrame>
      <h1 style={{ marginBottom: 24 }}>F0 primitives — dev showcase</h1>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
        <Card style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Toast</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn onClick={() => toast.success('Opgeslagen.')}>success</Btn>
            <Btn onClick={() => toast.error('Iets ging mis.')}>error</Btn>
            <Btn onClick={() => toast.warning('Let op.')}>warning</Btn>
            <Btn onClick={() => toast.info('Ter info.')}>info</Btn>
          </div>
        </Card>

        <Card style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Dialog</h3>
          <Dialog>
            <DialogTrigger asChild>
              <Btn variant="primary">Open dialoog</Btn>
            </DialogTrigger>
            <DialogContent>
              <DialogTitle>Voorbeeld-dialoog</DialogTitle>
              <DialogDescription>Beschrijft kort wat er gaat gebeuren.</DialogDescription>
              <DialogBody>
                <p>Lichaam van het dialoogvenster. Klik buiten of druk op Esc om te sluiten.</p>
              </DialogBody>
              <DialogFooter>
                <DialogClose asChild>
                  <Btn>Sluiten</Btn>
                </DialogClose>
                <Btn variant="primary">Hoofdactie</Btn>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Card>

        <Card style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>ConfirmDialog</h3>
          <Btn variant="ghost" className="c-bad" onClick={() => setConfirmOpen(true)}>
            <Icon name="trash" size={14} /> Verwijder iets
          </Btn>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Weet je het zeker?"
            description="Deze actie kan niet ongedaan worden gemaakt."
            confirmLabel="Verwijder"
            destructive
            onConfirm={() => {
              toast.success('Verwijderd.');
              setConfirmOpen(false);
            }}
          />
        </Card>

        <Card style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Form (RHF + Zod)</h3>
          <Form
            schema={z.object({
              name: z.string().min(2, 'Minstens 2 tekens'),
              email: z.string().email('Geen geldig e-mailadres'),
            })}
            defaultValues={{ name: '', email: '' }}
            onSubmit={(values) => {
              toast.success(`Submit: ${JSON.stringify(values)}`);
            }}
          >
            <FormField name="name" label="Naam" required>
              {(field) => <Input {...field} placeholder="Volledige naam" />}
            </FormField>
            <FormField name="email" label="E-mail" required>
              {(field) => <Input {...field} type="email" placeholder="naam@bedrijf.be" />}
            </FormField>
            <FormSubmitButton>Verstuur</FormSubmitButton>
          </Form>
        </Card>

        <Card style={{ padding: 20, gridColumn: '1 / -1' }}>
          <h3 style={{ marginBottom: 12 }}>File upload (drag-drop + picker)</h3>
          <input {...upload.inputProps} />
          <button
            type="button"
            {...upload.dropZoneProps}
            onClick={upload.openPicker}
            style={{
              width: '100%',
              padding: 24,
              border: '2px dashed var(--line-strong)',
              borderRadius: 'var(--radius)',
              background: upload.isDragging ? 'var(--accent-tint)' : 'var(--paper-2)',
              cursor: 'pointer',
              textAlign: 'center',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            <Icon name="upload" size={18} /> Sleep bestanden hier of klik om te selecteren
          </button>
          {upload.error && (
            <div role="alert" className="c-bad" style={{ marginTop: 8 }}>
              {upload.error}
            </div>
          )}
          {upload.files.length > 0 && (
            <ul style={{ marginTop: 12, paddingLeft: 20 }}>
              {upload.files.map((f) => (
                <li key={f.name} className="mono">
                  {f.name} — {Math.round(f.size / 1024)} KB
                </li>
              ))}
            </ul>
          )}
          {upload.previews.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {upload.previews.map((p) => (
                <img
                  key={p.url}
                  src={p.url}
                  alt={p.file.name}
                  width={120}
                  height={120}
                  style={{ borderRadius: 'var(--radius)', objectFit: 'cover' }}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </AppFrame>
  );
}
